import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { getLocalizedValue } from '../config/languageConfig';
import { ProductSection } from '../types/product';

interface SectionNavigationBarProps {
  sections: ProductSection[];
  onSectionClick: (sectionId: string) => void;
  activeSectionId?: string;
}

const SectionNavigationBar = forwardRef<HTMLDivElement, SectionNavigationBarProps>(({
  sections,
  onSectionClick,
  activeSectionId
}, ref) => {
  const [activeSection, setActiveSection] = useState<string>(activeSectionId || '');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // 활성 섹션이 변경될 때 상태 업데이트 및 자동 스크롤
  useEffect(() => {
    if (activeSectionId && activeSectionId !== activeSection) {
      setActiveSection(activeSectionId);
      scrollToActiveButton(activeSectionId);
    }
  }, [activeSectionId, activeSection]);

  // 활성 버튼으로 자동 스크롤하는 함수
  const scrollToActiveButton = (sectionId: string) => {
    const container = scrollContainerRef.current;
    const button = buttonRefs.current[sectionId];
    
    if (container && button) {
      // 컨테이너 기준으로 버튼의 상대적 위치 계산
      const buttonLeft = button.offsetLeft;
      const buttonWidth = button.offsetWidth;
      const containerWidth = container.clientWidth;
      const currentScrollLeft = container.scrollLeft;
      
      // 버튼이 화면에 완전히 보이는지 확인
      const isButtonVisible = 
        buttonLeft >= currentScrollLeft && 
        buttonLeft + buttonWidth <= currentScrollLeft + containerWidth;
      
      if (!isButtonVisible) {
        // 버튼을 중앙에 위치시키도록 스크롤
        const targetScrollLeft = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
        
        container.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: 'smooth'
        });
      }
    }
  };

  // 섹션 클릭 핸들러
  const handleSectionClick = (sectionId: string) => {
    setActiveSection(sectionId);
    onSectionClick(sectionId);
    scrollToActiveButton(sectionId);
  };

  // 버튼 ref 설정 함수
  const setButtonRef = (sectionId: string) => (el: HTMLButtonElement | null) => {
    buttonRefs.current[sectionId] = el;
  };

  // 현재 언어에 맞는 섹션 이름 가져오기
  const getSectionDisplayName = (section: ProductSection): string => {
    return getLocalizedValue(section.nameKo || section.name, {
      en: section.nameEn || section.name,
      ja: section.nameJa || section.name,
      'zh-CN': section.nameZhCn || section.name,
      'zh-TW': section.nameZhTw || section.name
    });
  };

  if (sections.length === 0) {
    return null;
  }

  return (
    <div ref={ref} className="sticky top-14 z-30 bg-white border-b border-gray-200 shadow-sm">
      <div 
        ref={scrollContainerRef}
        className="flex overflow-x-auto scrollbar-hide px-4 py-3"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {sections.map((section, index) => {
          const isActive = activeSection === section.id;
          const displayName = getSectionDisplayName(section);
          
          return (
            <button
              key={section.id}
              ref={setButtonRef(section.id)}
              onClick={() => handleSectionClick(section.id)}
              className={`
                flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                ${index > 0 ? 'ml-2' : ''}
                ${isActive 
                  ? 'bg-red-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
                }
              `}
            >
              {displayName}
            </button>
          );
        })}
      </div>
    </div>
  );
});

SectionNavigationBar.displayName = 'SectionNavigationBar';

export default SectionNavigationBar;
